import { uiPaper } from "shared/ui/uiSurface"
import {
  CheckCircle,
  Close,
  Info,
  LocalShipping,
  Refresh,
  Visibility,
} from "@mui/icons-material"
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material"
import dayjs from "dayjs"
import { FC, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CourierLinkItem,
  useAcceptLinkMutation,
  useDeleteLinkMutation,
  useReactivateLinkMutation,
  useUpdateLinkConfigMutation,
} from "../../apis/appApi"
import { getAvatarColor } from "../customers/utils"

type Props = {
  link: CourierLinkItem | null
  open: boolean
  onClose: () => void
  onAccept: (link: CourierLinkItem) => void
  onDelete: (link: CourierLinkItem) => void
}

const InfoRow: FC<{ label: string; value: React.ReactNode; "data-testid"?: string }> = ({
  label,
  value,
  "data-testid": testId,
}) => (
  <Stack direction="row" spacing={1} data-testid={testId}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
      {label}:
    </Typography>
    <Typography variant="body2">{value ?? "\u2014"}</Typography>
  </Stack>
)

const SectionHeader: FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
    <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
    <Typography variant="subtitle2" fontWeight={600}>
      {label}
    </Typography>
  </Stack>
)

function formatDate(raw: string | number | null | undefined): string {
  if (!raw) return "\u2014"
  return dayjs(raw).format("MMM D, YYYY")
}

function getStatusChipSx(state: string) {
  switch (state) {
    case "ESTABLISHED":
      return { bgcolor: "#e8f5e9", color: "#2e7d32" }
    case "INITIATED_WAITING_HAULER":
    case "INITIATED_WAITING_PROVIDER":
      return { bgcolor: "#fff3e0", color: "#e65100" }
    case "REFUSED":
      return { bgcolor: "#ffebee", color: "#c62828" }
    default:
      return {}
  }
}

type PositionVisibility = "ALWAYS" | "ONLY_WHEN_CARRYING" | "NEVER"
type EtaVisibility = "ALWAYS" | "NEVER"
type NumberOrderInCar = "ALL" | "NONE"

export const LinkDetailDrawer: FC<Props> = ({ link, open, onClose, onAccept, onDelete }) => {
  const { t } = useTranslation()
  const [acceptLink] = useAcceptLinkMutation()
  const [deleteLink] = useDeleteLinkMutation()
  const [updateLinkConfig, { isLoading: isSaving }] = useUpdateLinkConfigMutation()
  const [reactivateLink] = useReactivateLinkMutation()
  const [acceptConfirmOpen, setAcceptConfirmOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [reactivateConfirmOpen, setReactivateConfirmOpen] = useState(false)

  // Local config state for editable dropdowns
  const [positionVisibility, setPositionVisibility] = useState<PositionVisibility>("ALWAYS")
  const [etaVisibility, setEtaVisibility] = useState<EtaVisibility>("ALWAYS")
  const [numberOrderInCar, setNumberOrderInCar] = useState<NumberOrderInCar>("NONE")

  // Sync local state when link changes
  useEffect(() => {
    if (link?.config) {
      setPositionVisibility(link.config.positionVisibility)
      setEtaVisibility(link.config.etaVisibility)
      setNumberOrderInCar(link.config.numberOrderInCar)
    } else {
      setPositionVisibility("ALWAYS")
      setEtaVisibility("ALWAYS")
      setNumberOrderInCar("NONE")
    }
  }, [link?.id, link?.config])

  const configDirty =
    link != null &&
    (positionVisibility !== (link.config?.positionVisibility ?? "ALWAYS") ||
      etaVisibility !== (link.config?.etaVisibility ?? "ALWAYS") ||
      numberOrderInCar !== (link.config?.numberOrderInCar ?? "NONE"))

  const handleAccept = useCallback(async () => {
    if (!link) return
    try {
      await acceptLink(link.id).unwrap()
      setAcceptConfirmOpen(false)
      onAccept(link)
    } catch (error) {
      console.error("Failed to accept link:", error)
    }
  }, [link, acceptLink, onAccept])

  const handleDelete = useCallback(async () => {
    if (!link) return
    try {
      await deleteLink(link.id).unwrap()
      setDeleteConfirmOpen(false)
      onDelete(link)
      onClose()
    } catch (error) {
      console.error("Failed to delete link:", error)
    }
  }, [link, deleteLink, onDelete, onClose])

  const handleReactivate = useCallback(async () => {
    if (!link) return
    try {
      await reactivateLink(link.id).unwrap()
      setReactivateConfirmOpen(false)
      onClose()
    } catch (error) {
      console.error("Failed to reactivate link:", error)
    }
  }, [link, reactivateLink, onClose])

  const handleSaveConfig = useCallback(async () => {
    if (!link) return
    try {
      await updateLinkConfig({
        id: link.id,
        positionVisibility,
        etaVisibility,
        numberOrderInCar,
      }).unwrap()
    } catch (error) {
      console.error("Failed to save config:", error)
    }
  }, [link, updateLinkConfig, positionVisibility, etaVisibility, numberOrderInCar])

  const haulerName = link?.hauler?.name ?? ""
  const initial = haulerName.charAt(0)?.toUpperCase() || "?"

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        data-testid="link-detail-drawer"
        slotProps={{
          paper: {
            ...uiPaper("UI-DLV2-132"),
            sx: { width: 550, maxWidth: "100vw", display: "flex", flexDirection: "column" },
          },
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            {link && (
              <Avatar
                sx={{
                  width: 52,
                  height: 52,
                  fontSize: 20,
                  fontWeight: 600,
                  bgcolor: getAvatarColor(haulerName),
                }}
              >
                {initial}
              </Avatar>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap>
                {haulerName || t("loading")}
              </Typography>
              {link?.hauler?.code && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {link.hauler.code}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" data-testid="link-drawer-close-btn">
            <Close />
          </IconButton>
        </Stack>

        {link && (
          <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
            {/* Hauler Info */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <SectionHeader
                icon={<LocalShipping fontSize="small" />}
                label={t("courierLinks.drawer.haulerInfo")}
              />
              <Stack spacing={1.5}>
                <InfoRow
                  label={t("courierLinks.col.address")}
                  value={link.hauler.address || "\u2014"}
                  data-testid="link-detail-address"
                />
                <InfoRow
                  label={t("courierLinks.col.contact")}
                  value={
                    link.acceptingUser
                      ? `${link.acceptingUser.firstName} ${link.acceptingUser.lastName}`
                      : "\u2014"
                  }
                  data-testid="link-detail-contact"
                />
                <InfoRow
                  label="Code"
                  value={link.hauler.code}
                  data-testid="link-detail-code"
                />
              </Stack>
            </Paper>

            {/* Link Status */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <SectionHeader
                icon={<Info fontSize="small" />}
                label={t("courierLinks.drawer.linkStatus")}
              />
              <Stack spacing={1.5}>
                <Box>
                  <Chip
                    label={
                      link.state === "ESTABLISHED"
                        ? t("courierLinks.status.established")
                        : link.state === "INITIATED_WAITING_HAULER"
                          ? t("courierLinks.status.waitingHauler")
                          : link.state === "INITIATED_WAITING_PROVIDER"
                            ? t("courierLinks.status.waitingProvider")
                            : t("courierLinks.status.refused")
                    }
                    size="small"
                    sx={getStatusChipSx(link.state)}
                    data-testid="link-detail-status"
                  />
                </Box>
                <InfoRow
                  label="Initiated"
                  value={formatDate(link.initiationDate)}
                  data-testid="link-detail-initiation-date"
                />
                {link.acceptedDate && (
                  <InfoRow
                    label="Established"
                    value={formatDate(link.acceptedDate)}
                    data-testid="link-detail-accepted-date"
                  />
                )}
              </Stack>
            </Paper>

            {/* Visibility Settings — Editable */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <SectionHeader
                icon={<Visibility fontSize="small" />}
                label={t("courierLinks.drawer.visibility")}
              />
              <Stack spacing={2}>
                <FormControl size="small" fullWidth>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t("courierLinks.drawer.position")}
                  </Typography>
                  <Select
                    value={positionVisibility}
                    onChange={(e) => setPositionVisibility(e.target.value as PositionVisibility)}
                    data-testid="link-config-position"
                  >
                    <MenuItem value="ALWAYS">{t("courierLinks.config.position.always")}</MenuItem>
                    <MenuItem value="ONLY_WHEN_CARRYING">
                      {t("courierLinks.config.position.whenCarrying")}
                    </MenuItem>
                    <MenuItem value="NEVER">{t("courierLinks.config.position.never")}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t("courierLinks.drawer.eta")}
                  </Typography>
                  <Select
                    value={etaVisibility}
                    onChange={(e) => setEtaVisibility(e.target.value as EtaVisibility)}
                    data-testid="link-config-eta"
                  >
                    <MenuItem value="ALWAYS">{t("courierLinks.config.eta.always")}</MenuItem>
                    <MenuItem value="NEVER">{t("courierLinks.config.eta.never")}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t("courierLinks.drawer.orderCount")}
                  </Typography>
                  <Select
                    value={numberOrderInCar}
                    onChange={(e) => setNumberOrderInCar(e.target.value as NumberOrderInCar)}
                    data-testid="link-config-order-count"
                  >
                    <MenuItem value="ALL">{t("courierLinks.config.orderCount.all")}</MenuItem>
                    <MenuItem value="NONE">{t("courierLinks.config.orderCount.none")}</MenuItem>
                  </Select>
                </FormControl>
                {configDirty && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveConfig}
                    disabled={isSaving}
                    data-testid="link-config-save-btn"
                  >
                    {isSaving
                      ? t("courierLinks.config.saving")
                      : t("courierLinks.config.save")}
                  </Button>
                )}
              </Stack>
            </Paper>

            {/* Actions */}
            <Stack direction="row" spacing={1}>
              {link.state === "INITIATED_WAITING_PROVIDER" && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CheckCircle />}
                  data-testid="link-drawer-accept-btn"
                  onClick={() => setAcceptConfirmOpen(true)}
                  sx={{ bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}
                >
                  {t("courierLinks.accept.title")}
                </Button>
              )}
              {link.state === "REFUSED" && (
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<Refresh />}
                  data-testid="link-drawer-reactivate-btn"
                  onClick={() => setReactivateConfirmOpen(true)}
                >
                  {t("courierLinks.reactivate.title")}
                </Button>
              )}
              {link.state !== "REFUSED" && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  data-testid="link-drawer-delete-btn"
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  {t("courierLinks.delete.title")}
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </Drawer>

      {/* Accept confirmation */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-133") }}
        open={acceptConfirmOpen}
        onClose={() => setAcceptConfirmOpen(false)}
        maxWidth="xs"
        data-testid="link-accept-dialog"
      >
        <DialogTitle>{t("courierLinks.accept.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("courierLinks.accept.message", { name: haulerName })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAcceptConfirmOpen(false)}
            data-testid="link-accept-cancel-btn"
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleAccept}
            variant="contained"
            data-testid="link-accept-confirm-btn"
            sx={{ bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}
          >
            {t("courierLinks.accept.title")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-134") }}
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        data-testid="link-delete-dialog"
      >
        <DialogTitle>{t("courierLinks.delete.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("courierLinks.delete.message", { name: haulerName })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            data-testid="link-delete-cancel-btn"
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            data-testid="link-delete-confirm-btn"
          >
            {t("courierLinks.delete.title")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reactivate confirmation */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-135") }}
        open={reactivateConfirmOpen}
        onClose={() => setReactivateConfirmOpen(false)}
        maxWidth="xs"
        data-testid="link-reactivate-dialog"
      >
        <DialogTitle>{t("courierLinks.reactivate.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("courierLinks.reactivate.message", { name: haulerName })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReactivateConfirmOpen(false)}
            data-testid="link-reactivate-cancel-btn"
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleReactivate}
            variant="contained"
            data-testid="link-reactivate-confirm-btn"
          >
            {t("courierLinks.reactivate.title")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
